import styles from '../Account.module.css';
import AccountCard, { InfoRow } from '../AccountCard';
import SectionHead from '../SectionHead';
import StatusBadge from '../StatusBadge';
import EmptyState from '../EmptyState';
import TrialCallout from '../TrialCallout';
import SupportCard from '../SupportCard';
import Icon from '../Icon';
import { formatMoney, formatDate, renewalInfo } from '../../../services/billingService';

/**
 * Billing - the central billing overview. Summarizes the subscription, payment
 * method and recent invoices, and offers the primary self-service actions
 * (Change Plan, Change Billing Cycle, Cancel). The invoice card is a compact summary
 * linking to the Invoices tab; the payment-method card is the only place the card is
 * shown at all, and its button opens the Stripe Portal, which is the only place a card
 * can actually be changed. Cancel opens the shared confirmation dialog owned by
 * AccountPage, so no business logic is duplicated here.
 */
export default function BillingSection({
  billing,
  onChangePlan,
  onChangeBillingCycle,
  onManagePayment,
  onViewInvoices,
  onCancel,
  onResume,
  onChoosePlan,
  onContact,
}) {
  const { identity, subscription: sub, trial, status } = billing || {};
  const pm = billing?.paymentMethods?.find((p) => p.isDefault) || billing?.paymentMethods?.[0];
  const invoices = billing?.invoices || [];
  const canceled = sub?.canceled;
  const isFounding = identity?.isFounding;

  const trialLabel = trial?.status === 'pending'
    ? `Starts ${formatDate(trial.startsAt)}`
    : trial?.status === 'active'
      ? `${trial.daysLeft} ${trial.daysLeft === 1 ? 'day' : 'days'} left`
      : trial?.status === 'ended' ? 'Ended' : 'None';
  const renewal = renewalInfo(sub, trial);

  return (
    <>
      <SectionHead title="Billing" subtitle="Your subscription, payment method and invoices at a glance." />

      <TrialCallout trial={trial} isFounding={isFounding} />

      {/* ── Subscription summary ── */}
      {sub ? (
        <AccountCard
          title="Subscription summary"
          description="Your current plan, billing status and renewal details."
          actions={<StatusBadge status={status} />}
        >
          <div className={styles.planRow}>
            <div>
              <div className={styles.planName}>
                {isFounding ? `Founding ${sub.plan}` : sub.plan}
                {isFounding && <span className={styles.foundingBadge}>👑 Price locked</span>}
              </div>
              <div className={styles.planMeta}>
                {sub.billing === 'Annual' ? 'Billed annually' : 'Billed monthly'}
                {sub.priceNote ? ` · ${sub.priceNote}` : ''}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span className={styles.planPrice}>{formatMoney(sub.price, sub.currency)}</span>
              <span className={styles.planPriceUnit}>{sub.unit}</span>
            </div>
          </div>

          <div className={styles.infoGrid}>
            <InfoRow label="Billing cycle" icon={<Icon name="calendar" size={13} />}>{sub.billing}</InfoRow>
            <InfoRow label="Trial status" icon={<Icon name="zap" size={13} />}>{trialLabel}</InfoRow>
            <InfoRow label={renewal.label} icon={<Icon name="clock" size={13} />}>{formatDate(renewal.date)}</InfoRow>
            <InfoRow label="Subscription status" icon={<Icon name="activity" size={13} />}>
              <StatusBadge status={status} />
            </InfoRow>
          </div>

          <div className={styles.divider} />

          {/* Self-service actions. Cancel is always clearly visible. */}
          {canceled ? (
            <div className={styles.cardActions}>
              <button className={styles.btnPrimary} onClick={onResume}>Resume Subscription</button>
            </div>
          ) : (
            <>
              <div className={styles.cardActions}>
                <button className={styles.btnGhost} onClick={onChangePlan}>Change Plan</button>
                <button className={styles.btnGhost} onClick={onChangeBillingCycle}>Change Billing Cycle</button>
                <button className={styles.btnDanger} onClick={onCancel}>Cancel Subscription</button>
              </div>
              <p className={styles.note}>
                Cancel online any time - you keep access until the end of your
                {trial?.status === 'active' || trial?.status === 'pending' ? ' trial' : ' billing period'}.
                No emails or support tickets required.
              </p>
            </>
          )}
        </AccountCard>
      ) : (
        <AccountCard title="Subscription summary" description="You don't have an active plan yet.">
          <EmptyState
            icon={<Icon name="package" size={20} />}
            title="No active subscription"
            description="Pick a plan to start your free trial. You won't be charged until the trial ends."
            action={<button className={styles.btnPrimary} onClick={onChoosePlan}>Choose a plan</button>}
          />
        </AccountCard>
      )}

      {/* ── Payment method ──
          This is now the only place the card is shown. The dedicated Payment Method tab was
          removed: it displayed the same brand/last4/expiry row as this card and added one
          button, and that button only opened the Stripe Portal. A whole tab to duplicate a
          summary and forward a click was a step with nothing in it, so the button lives here
          and goes straight to Stripe. RepBuddy never stores card details, so there is no
          richer view to lose. */}
      <AccountCard
        title="Payment method"
        description="The card charged when billing begins. Managed securely by Stripe."
        actions={(
          <button className={`${styles.btnGhost} ${styles.btnSmall}`} onClick={onManagePayment}>
            {pm ? 'Update card in Stripe' : 'Manage in Stripe'}
          </button>
        )}
      >
        {pm ? (
          <div className={styles.pmRow}>
            <div className={styles.pmLeft}>
              <div className={styles.pmBrand} aria-hidden="true"><Icon name="creditCard" size={16} /></div>
              <div>
                <div className={styles.pmNum}>{pm.brand} •••• {pm.last4}</div>
                <div className={styles.pmExp}>Expires {String(pm.expMonth).padStart(2, '0')}/{pm.expYear}</div>
              </div>
            </div>
            <StatusBadge status="active" label="Default" />
          </div>
        ) : (
          <div className={styles.miniEmpty}>
            <span className={styles.miniEmptyIcon} aria-hidden="true"><Icon name="wallet" size={15} /></span>
            No payment method on file.
          </div>
        )}
      </AccountCard>

      {/* ── Recent invoices summary (always shown - links to the Invoices tab) ── */}
      <AccountCard
        title="Recent invoices"
        description="Your latest billing history."
        actions={<button className={`${styles.btnGhost} ${styles.btnSmall}`} onClick={onViewInvoices}>View Invoices</button>}
      >
        {invoices.length > 0 ? (
          invoices.slice(0, 3).map((inv) => (
            <div className={styles.infoRow} key={inv.id}>
              <span className={styles.infoValue}>{formatDate(inv.date)}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span className={styles.infoValue}>{formatMoney(inv.amount)}</span>
                <StatusBadge status={inv.status} />
              </span>
            </div>
          ))
        ) : (
          <div className={styles.miniEmpty}>
            <span className={styles.miniEmptyIcon} aria-hidden="true"><Icon name="fileText" size={15} /></span>
            No invoices available yet.
          </div>
        )}
      </AccountCard>

      {/* ── Support ── */}
      <SupportCard onContact={onContact} />
    </>
  );
}
